import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["activePage", "arrowControls", "className", "fwdRef", "hideSegments", "indicators", "numberedControlLabels", "onChange", "pageCount", "segmentCount", "size", "style"];
import React, { useMemo, useReducer, forwardRef } from 'react';
import { createPageItems } from './_createPageItems';
import { useUpdateEffect } from '../common/hooks';
import { useLocale } from '../common/locale';
import { buildVersionedClassNames } from '@op/opds.opux-utilities';
import { getMessages } from './_messages';
const MIN_SEGMENTS = 7;
import PaginationSegments from './_paginationSegments';
import FirstAndPreviousButtons from './_firstAndPreviousButtons';
import LastAndNextButtons from './_lastAndNextButtons';
import ScreenReaderOnly from '../screen-reader-only/screen-reader-only';
function reducer(activePage, action) {
  switch (action.type) {
    case 'NEXT':
      {
        if (action.pageCount) {
          return Math.min(activePage + 1, action.pageCount - 1);
        }
        return activePage;
      }
    case 'PREVIOUS':
      return Math.max(0, activePage - 1);
    case 'FIRST':
      return 0;
    case 'LAST':
      {
        if (action.pageCount) {
          return action.pageCount - 1;
        }
        return activePage;
      }
    case 'PAGE':
      {
        // Check can't be just "if (action.index)" because 0 is a valid value
        if (action.index !== undefined) {
          return action.index;
        }
        return activePage;
      }
    default:
      return activePage;
  }
}

/**
 * _Pagination component enables the user to navigate between pages of paginated content._
 *
 * ***
 *
 * Dividing a large amount of content into smaller paginated portions
 * can make it more manageable for the user. Pagination also allows the
 * user to easily continue to other interests.
 *
 * The Pagination component provides controls to navigate between the
 * pages of the content, helps to understand the total amount of it and
 * where one is at on it.
 *
 * The component props allow controlling what elements it provides for
 * the user. The component itself counts page numbers. It doesn't
 * implement the actual division of content into pages.
 *
 * Additional props (including `ref`) will be forwarded to the wrapping `<nav>` element.
 */
export function Pagination(_ref) {
  let {
      activePage: propActivePage = 1,
      arrowControls = 'prevNext',
      className,
      fwdRef,
      hideSegments = false,
      indicators = false,
      numberedControlLabels,
      onChange,
      pageCount,
      segmentCount = MIN_SEGMENTS,
      size = 'default',
      style
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  // Throw an error to developer if segment count is set to under 7
  if (segmentCount < MIN_SEGMENTS && process.env.NODE_ENV !== 'production') {
    throw new Error('segmentCount should be 7 or more');
  }
  const lc = useLocale();
  const {
    firstButtonLabel,
    lastButtonLabel,
    nextButtonLabel,
    page: localizedPageLabel,
    pagination,
    previousButtonLabel,
    slash
  } = getMessages(lc);

  // Turn 1-based activePage to 0-based for internal usage
  const activePagePropIndex = propActivePage > pageCount || propActivePage < 1 ? 0 : propActivePage - 1;
  const [activePageIndex, dispatch] = useReducer(reducer, activePagePropIndex);
  const dispatchWithPageCount = action => dispatch(_extends({}, action, {
    pageCount
  }));
  const pages = useMemo(() => createPageItems(pageCount, activePageIndex, segmentCount, indicators), [activePageIndex, pageCount, segmentCount, indicators]);
  useUpdateEffect(() => {
    if (onChange) {
      onChange(activePageIndex + 1);
    }
  }, [activePageIndex]);
  useUpdateEffect(() => {
    // Update internal state if external `activePage` changes
    if (activePagePropIndex !== activePageIndex) {
      dispatchWithPageCount({
        index: activePagePropIndex,
        type: 'PAGE'
      });
    }
  }, [activePagePropIndex]);
  return /*#__PURE__*/React.createElement("nav", _extends({
    "aria-label": pagination,
    className: buildVersionedClassNames('ds-pagination', className, {
      ["ds-pagination--" + size]: size !== 'default' && !indicators
    }),
    ref: fwdRef,
    style: style
  }, props), indicators && /*#__PURE__*/React.createElement(ScreenReaderOnly, null, localizedPageLabel + " " + (activePageIndex + 1) + " " + slash + " " + pageCount), /*#__PURE__*/React.createElement(FirstAndPreviousButtons, {
    activePageIndex: activePageIndex,
    arrowControls: arrowControls,
    dispatchWithPageCount: dispatchWithPageCount,
    firstButtonLabel: firstButtonLabel,
    previousButtonLabel: previousButtonLabel,
    size: size
  }), /*#__PURE__*/React.createElement(PaginationSegments, {
    activePageIndex: activePageIndex,
    arrowControls: arrowControls,
    dispatchWithPageCount: dispatchWithPageCount,
    hideSegments: hideSegments,
    indicators: indicators,
    localizedPageLabel: localizedPageLabel,
    numberedControlLabels: numberedControlLabels != null ? numberedControlLabels : [],
    pageCount: pageCount,
    pages: pages
  }), /*#__PURE__*/React.createElement(LastAndNextButtons, {
    activePageIndex: activePageIndex,
    arrowControls: arrowControls,
    dispatchWithPageCount: dispatchWithPageCount,
    hideSegments: hideSegments,
    lastButtonLabel: lastButtonLabel,
    nextButtonLabel: nextButtonLabel,
    pageCount: pageCount,
    size: size
  }));
}
const Fwd = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(Pagination, _extends({}, props, {
  fwdRef: ref
})));
Fwd.displayName = 'Pagination';
export default Fwd;